import React, { useState, useMemo, useCallback, memo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// Import bookmaker logos
import sportsbetLogo from '../../assets/sb.png';
import tabLogo from '../../assets/tab.jpg';
import pointsbetLogo from '../../assets/pb.png';
import dabbleLogo from '../../assets/dabble.webp';
import nedsLogo from '../../assets/neds.jpg';
import betrightLogo from '../../assets/betright.jpg';
import tabtouchLogo from '../../assets/tabtouch.webp';
import bet365Logo from '../../assets/bet365.png';
import betmakersLogo from '../../assets/betmakers.png';
import ladbrokesLogo from '../../assets/ladbrokes.webp';
import betrLogo from '../../assets/betr.jpg';
import picklebetLogo from '../../assets/picklebet.jpeg';
import playupLogo from '../../assets/playup.png';
import unibetLogo from '../../assets/unibet.webp';

// Bookmaker options shared by forms (matches the allowed bookmaker set)
const BOOKIE_OPTIONS = ['Sportsbet', 'TAB', 'Pointsbet', 'Dabble', 'Neds', 'Betright', 'TABtouch', 'Bet365', 'Ladbrokes', 'Betmakers', 'Betr', 'Picklebet', 'PlayUp', 'Unibet', 'Other'];

// Tools that can produce a community tip
export const TOOL_OPTIONS = ['DISCORD_BOT', 'SNIPE_FEED', 'ODDS_SCREEN', 'HOT_PROPS', 'MULTI_BUILDER'];
export const TOOL_LABELS = {
    DISCORD_BOT: 'Discord Bot',
    SNIPE_FEED: 'Snipe Feed',
    ODDS_SCREEN: 'Odds Screen',
    HOT_PROPS: 'Hot Props',
    MULTI_BUILDER: 'Multi Builder'
};

const bookieLogos = {
    'Sportsbet': sportsbetLogo,
    'TAB': tabLogo,
    'Pointsbet': pointsbetLogo,
    'Dabble': dabbleLogo,
    'Neds': nedsLogo,
    'Betright': betrightLogo,
    'TABtouch': tabtouchLogo,
    'Bet365': bet365Logo,
    'bet365': bet365Logo,
    'Ladbrokes': ladbrokesLogo,
    'Betmakers': betmakersLogo,
    'Betr': betrLogo,
    'Picklebet': picklebetLogo,
    'PlayUp': playupLogo,
    'Unibet': unibetLogo,
};

// Resolve a friendly tipper label from a tip object
const getTipperLabel = (tip) =>
    tip.tipper_discord_name || tip.tipper_email || (tip.user_id != null ? `User #${tip.user_id}` : 'Unknown');

// Format a unit value accurately (up to 2 dp) and strip trailing zeros so e.g.
// 1 -> "1", 1.8 -> "1.8", 0.25 -> "0.25", 4.7 -> "4.7". Avoids the rounding
// drift that a fixed 1-decimal format introduces when summing fractional units.
const cleanUnits = (n) => {
    const rounded = Math.round((Number(n) + Number.EPSILON) * 100) / 100;
    return rounded.toFixed(2).replace(/\.?0+$/, '');
};

// Add Tip Form — single tip on behalf of a chosen tipper.
const AddTipForm = memo(function AddTipForm({ onSubmit, onCancel, sportOptions, tippers = [], isSubmitting, isUnitMode }) {
    const createEmptyTip = () => ({
        user_id: tippers[0]?.user_id != null ? String(tippers[0].user_id) : '',
        tipped_tool: 'DISCORD_BOT',
        date: new Date().toISOString().split('T')[0],
        sport: sportOptions[0]?.value || 'afl',
        game: '',
        bet: '',
        odds: '',
        stake: '',
        payout: '',
        bookie: '',
        result: 'pending'
    });

    const [tip, setTip] = useState(createEmptyTip());

    const updateField = (field, value) => {
        setTip(prev => {
            const updated = { ...prev, [field]: value };
            if (field === 'odds' || field === 'stake') {
                const odds = field === 'odds' ? parseFloat(value) : parseFloat(prev.odds);
                const stake = field === 'stake' ? parseFloat(value) : parseFloat(prev.stake);
                if (odds && stake) {
                    updated.payout = (odds * stake).toFixed(2);
                }
            }
            return updated;
        });
    };

    const isValid = tip.user_id !== '' && tip.tipped_tool && tip.game && tip.bet && tip.odds && tip.stake && tip.bookie && tip.sport;

    const handleSubmit = async () => {
        if (!isValid) return;
        const result = await onSubmit({
            user_id: parseInt(tip.user_id, 10),
            tipped_tool: tip.tipped_tool,
            date: tip.date,
            sport: tip.sport,
            game: tip.game,
            bet: tip.bet,
            odds: parseFloat(tip.odds),
            stake: parseFloat(tip.stake),
            payout: parseFloat(tip.payout) || 0,
            bookie: tip.bookie,
            result: tip.result
        });
        if (result?.success) {
            setTip(createEmptyTip());
        }
    };

    const fieldClass = 'px-2 py-2 bg-slate-900 border border-slate-700 rounded-lg text-xs text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500';

    return (
        <div className="p-4 md:p-6 bg-slate-800/50 border-b border-slate-700/50">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {/* Tipper */}
                <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Tipper</span>
                    {tippers.length > 0 ? (
                        <select value={tip.user_id} onChange={(e) => updateField('user_id', e.target.value)} className={fieldClass}>
                            {tippers.map(t => (
                                <option key={t.user_id} value={t.user_id}>{t.label}</option>
                            ))}
                        </select>
                    ) : (
                        <input type="number" min="1" step="1" placeholder="Tipper user id" value={tip.user_id} onChange={(e) => updateField('user_id', e.target.value)} className={fieldClass} />
                    )}
                </div>

                {/* Tool */}
                <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Tool</span>
                    <select value={tip.tipped_tool} onChange={(e) => updateField('tipped_tool', e.target.value)} className={fieldClass}>
                        {TOOL_OPTIONS.map(t => <option key={t} value={t}>{TOOL_LABELS[t]}</option>)}
                    </select>
                </div>

                {/* Date */}
                <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Date</span>
                    <input type="date" value={tip.date} onChange={(e) => updateField('date', e.target.value)} className={`${fieldClass} [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer`} />
                </div>

                {/* Sport */}
                <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Sport</span>
                    <select value={tip.sport} onChange={(e) => updateField('sport', e.target.value)} className={fieldClass}>
                        {sportOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                </div>

                {/* Game */}
                <div className="flex flex-col gap-1 col-span-2">
                    <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Game</span>
                    <input type="text" placeholder="Match / fixture" value={tip.game} onChange={(e) => updateField('game', e.target.value)} className={fieldClass} />
                </div>

                {/* Bet */}
                <div className="flex flex-col gap-1 col-span-2">
                    <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Bet</span>
                    <input type="text" placeholder="Selection" value={tip.bet} onChange={(e) => updateField('bet', e.target.value)} className={fieldClass} />
                </div>

                {/* Odds */}
                <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Odds</span>
                    <input type="number" step="0.01" placeholder="Odds" value={tip.odds} onChange={(e) => updateField('odds', e.target.value)} className={fieldClass} />
                </div>

                {/* Stake */}
                <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">{isUnitMode ? 'Stake (U)' : 'Stake'}</span>
                    <input type="number" step="0.01" placeholder="Stake" value={tip.stake} onChange={(e) => updateField('stake', e.target.value)} className={fieldClass} />
                </div>

                {/* Payout */}
                <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Payout</span>
                    <input type="number" step="0.01" placeholder="Payout" value={tip.payout} readOnly className={`${fieldClass} bg-slate-900/50 text-slate-400 cursor-not-allowed`} title="Auto-calculated: Odds × Stake" />
                </div>

                {/* Bookie */}
                <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Bookie</span>
                    <select value={tip.bookie} onChange={(e) => updateField('bookie', e.target.value)} className={fieldClass}>
                        <option value="">Bookie</option>
                        {BOOKIE_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                </div>

                {/* Result */}
                <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Result</span>
                    <select value={tip.result} onChange={(e) => updateField('result', e.target.value)} className={fieldClass}>
                        <option value="pending">Pending</option>
                        <option value="win">Win</option>
                        <option value="loss">Loss</option>
                        <option value="cash out">Cash Out</option>
                    </select>
                </div>
            </div>

            <div className="mt-4 flex items-center justify-end gap-2">
                <button onClick={onCancel} disabled={isSubmitting} className="px-4 py-2 text-slate-400 hover:text-white transition-colors text-sm disabled:opacity-50">Cancel</button>
                <button onClick={handleSubmit} disabled={isSubmitting || !isValid} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                    {isSubmitting ? (<><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Adding...</>) : 'Add Tip'}
                </button>
            </div>
        </div>
    );
});

function CommunityBetTracker({ tips, tippers = [], onAddBet, onUpdateBet, onDeleteBet, onReorderBets, displayMode = 'units', unitSize = 1, isSaving = false, sportOptions = [] }) {
    const isUnitMode = displayMode === 'units';

    const [isAdding, setIsAdding] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editTip, setEditTip] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });
    const [filterResult, setFilterResult] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [updatingResultId, setUpdatingResultId] = useState(null);

    // Drag and drop state (reorder scoped to tipper + date)
    const [draggedTip, setDraggedTip] = useState(null);
    const [dropTargetId, setDropTargetId] = useState(null);
    const [isReordering, setIsReordering] = useState(false);

    // Cash out modal state
    const [showCashOutModal, setShowCashOutModal] = useState(false);
    const [cashOutTip, setCashOutTip] = useState(null);
    const [cashOutAmount, setCashOutAmount] = useState('');
    const cashOutInputRef = useRef(null);

    useEffect(() => {
        if (showCashOutModal) {
            document.body.style.overflow = 'hidden';
            setTimeout(() => cashOutInputRef.current?.focus({ preventScroll: true }), 50);
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [showCashOutModal]);

    const matchesSearch = useCallback((t, term) => {
        return (
            (t.game || '').toLowerCase().includes(term) ||
            (t.bet || '').toLowerCase().includes(term) ||
            (t.bookie || '').toLowerCase().includes(term) ||
            getTipperLabel(t).toLowerCase().includes(term) ||
            (TOOL_LABELS[t.tipped_tool] || t.tipped_tool || '').toLowerCase().includes(term)
        );
    }, []);

    const searchFilteredTips = useMemo(() => {
        if (!searchTerm) return tips;
        const term = searchTerm.toLowerCase();
        return tips.filter(t => matchesSearch(t, term));
    }, [tips, searchTerm, matchesSearch]);

    const processedTips = useMemo(() => {
        let filtered = [...tips];

        if (filterResult !== 'all') {
            filtered = filtered.filter(t => t.result === filterResult);
        }

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(t => matchesSearch(t, term));
        }

        filtered.sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            if (sortConfig.key === 'date') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            } else if (sortConfig.key === 'tipper') {
                aVal = getTipperLabel(a).toLowerCase();
                bVal = getTipperLabel(b).toLowerCase();
            }

            if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;

            const aSortOrder = a.sort_order ?? a.id;
            const bSortOrder = b.sort_order ?? b.id;
            return sortConfig.direction === 'asc' ? aSortOrder - bSortOrder : bSortOrder - aSortOrder;
        });

        return filtered;
    }, [tips, sortConfig, filterResult, searchTerm, matchesSearch]);

    const handleSort = useCallback((key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
    }, []);

    // Quick result change (resulting a tip) without full edit
    const handleQuickResultChange = async (tip, newResult) => {
        if (newResult === tip.result) return;

        if (newResult === 'cash out') {
            setCashOutTip(tip);
            setCashOutAmount('');
            setShowCashOutModal(true);
            return;
        }

        setUpdatingResultId(tip.id);

        const originalPayout = tip.stake * tip.odds;
        const newPL = newResult === 'win' ? originalPayout - tip.stake
            : newResult === 'loss' ? -tip.stake
            : 0;

        await onUpdateBet({
            id: tip.id,
            tipped_tool: tip.tipped_tool,
            date: tip.date,
            sport: tip.sport,
            game: tip.game,
            bet: tip.bet,
            odds: tip.odds,
            stake: tip.stake,
            payout: originalPayout,
            bookie: tip.bookie,
            result: newResult,
            pl: newPL
        });
        setUpdatingResultId(null);
    };

    const handleCashOutConfirm = async () => {
        if (!cashOutTip || cashOutAmount === '') return;
        const cashOutValue = parseFloat(cashOutAmount);
        if (isNaN(cashOutValue) || cashOutValue < 0) return;

        setUpdatingResultId(cashOutTip.id);
        setShowCashOutModal(false);

        await onUpdateBet({
            id: cashOutTip.id,
            tipped_tool: cashOutTip.tipped_tool,
            date: cashOutTip.date,
            sport: cashOutTip.sport,
            game: cashOutTip.game,
            bet: cashOutTip.bet,
            odds: cashOutTip.odds,
            stake: cashOutTip.stake,
            payout: cashOutValue,
            bookie: cashOutTip.bookie,
            result: 'cash out',
            pl: cashOutValue - cashOutTip.stake
        });
        setUpdatingResultId(null);
        setCashOutTip(null);
        setCashOutAmount('');
    };

    const handleCashOutCancel = () => {
        setShowCashOutModal(false);
        setCashOutTip(null);
        setCashOutAmount('');
    };

    const handleUpdateTip = async () => {
        if (!editTip) return;
        setIsUpdating(true);
        const result = await onUpdateBet({
            id: editTip.id,
            tipped_tool: editTip.tipped_tool,
            date: editTip.date,
            sport: editTip.sport,
            game: editTip.game,
            bet: editTip.bet,
            odds: parseFloat(editTip.odds),
            stake: parseFloat(editTip.stake),
            payout: parseFloat(editTip.payout) || 0,
            bookie: editTip.bookie,
            result: editTip.result
        });
        if (result?.success) {
            setEditingId(null);
            setEditTip(null);
        }
        setIsUpdating(false);
    };

    const handleDelete = async (id) => {
        setDeletingId(id);
        await onDeleteBet(id);
        setDeletingId(null);
    };

    const startEditing = (tip) => {
        setEditingId(tip.id);
        setEditTip({ ...tip });
    };

    const handleAddSubmit = useCallback(async (tipData) => {
        const result = await onAddBet(tipData);
        if (result?.success) {
            setIsAdding(false);
        }
        return result;
    }, [onAddBet]);

    // Group tips by tipper + date for reorder validation (scope is user_id + date)
    const tipsByGroup = useMemo(() => {
        const grouped = {};
        tips.forEach(tip => {
            const key = `${tip.user_id}__${tip.date}`;
            if (!grouped[key]) grouped[key] = [];
            grouped[key].push(tip);
        });
        Object.keys(grouped).forEach(key => {
            grouped[key].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
        });
        return grouped;
    }, [tips]);

    const canReorder = filterResult === 'all' && !searchTerm && sortConfig.key === 'date';

    const sameGroup = (a, b) => a.user_id === b.user_id && a.date === b.date;

    const handleDragStart = useCallback((e, tip) => {
        if (!canReorder) {
            e.preventDefault();
            return;
        }
        setDraggedTip(tip);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', tip.id.toString());
        setTimeout(() => { e.target.closest('tr')?.classList.add('opacity-50'); }, 0);
    }, [canReorder]);

    const handleDragEnd = useCallback((e) => {
        e.target.closest('tr')?.classList.remove('opacity-50');
        setDraggedTip(null);
        setDropTargetId(null);
    }, []);

    const handleDragOver = useCallback((e, tip) => {
        e.preventDefault();
        if (!draggedTip || draggedTip.id === tip.id) return;
        if (!sameGroup(draggedTip, tip)) {
            e.dataTransfer.dropEffect = 'none';
            return;
        }
        e.dataTransfer.dropEffect = 'move';
        setDropTargetId(tip.id);
    }, [draggedTip]);

    const handleDragLeave = useCallback(() => setDropTargetId(null), []);

    const handleDrop = useCallback(async (e, targetTip) => {
        e.preventDefault();
        setDropTargetId(null);

        if (!draggedTip || draggedTip.id === targetTip.id || !sameGroup(draggedTip, targetTip)) {
            setDraggedTip(null);
            return;
        }

        const key = `${draggedTip.user_id}__${draggedTip.date}`;
        const groupTips = [...(tipsByGroup[key] || [])];
        const draggedIndex = groupTips.findIndex(t => t.id === draggedTip.id);
        const targetIndex = groupTips.findIndex(t => t.id === targetTip.id);
        if (draggedIndex === -1 || targetIndex === -1) {
            setDraggedTip(null);
            return;
        }

        const [removed] = groupTips.splice(draggedIndex, 1);
        groupTips.splice(targetIndex, 0, removed);

        const betOrders = groupTips.map((t, index) => ({ id: t.id, sort_order: index + 1 }));

        setDraggedTip(null);
        setIsReordering(true);
        if (onReorderBets) {
            await onReorderBets(draggedTip.user_id, draggedTip.date, betOrders);
        }
        setIsReordering(false);
    }, [draggedTip, tipsByGroup, onReorderBets]);

    // Formatting helpers
    const formatCurrency = (value) => {
        if (value === undefined || value === null) return '-';
        if (isUnitMode) {
            const units = value / unitSize;
            const prefix = units >= 0 ? '+' : '-';
            return `${prefix}${cleanUnits(Math.abs(units))}U`;
        }
        const prefix = value >= 0 ? '+$' : '-$';
        return `${prefix}${Math.abs(value).toFixed(2)}`;
    };

    const formatStaked = (value) => {
        if (value === undefined || value === null) return '-';
        if (isUnitMode) {
            return `${cleanUnits(value / unitSize)}U`;
        }
        return `$${value.toFixed(2)}`;
    };

    const formatTotalStaked = (list) => {
        const total = list.reduce((sum, t) => sum + t.stake, 0);
        return isUnitMode ? `${cleanUnits(total / unitSize)}U` : `$${total.toFixed(2)}`;
    };

    const formatTotalPL = (list) => {
        const total = list.reduce((sum, t) => sum + t.pl, 0);
        if (isUnitMode) {
            const units = total / unitSize;
            return `${units >= 0 ? '+' : '-'}${cleanUnits(Math.abs(units))}U`;
        }
        return `${total >= 0 ? '+$' : '-$'}${Math.abs(total).toFixed(2)}`;
    };

    const getTotalPLValue = (list) => list.reduce((sum, t) => sum + t.pl, 0);

    const formatDate = (dateStr) =>
        new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    const SortIcon = ({ column }) => {
        if (sortConfig.key !== column) {
            return (
                <svg className="w-4 h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
            );
        }
        return sortConfig.direction === 'desc' ? (
            <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
        ) : (
            <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
            </svg>
        );
    };

    const editFieldClass = 'px-2 py-1 bg-slate-900 border border-slate-700 rounded text-sm text-white focus:outline-none focus:border-emerald-500 w-full min-w-0';

    const columns = [
        { key: 'tipper', label: 'Tipper', width: 'min-w-[120px]' },
        { key: 'tipped_tool', label: 'Tool', width: 'w-[120px]' },
        { key: 'date', label: 'Date', width: 'w-[100px]' },
        { key: 'game', label: 'Game', width: 'min-w-[140px]' },
        { key: 'bet', label: 'Bet', width: 'min-w-[220px]' },
        { key: 'odds', label: 'Odds', width: 'w-[70px]' },
        { key: 'stake', label: 'Stake', width: 'w-[80px]' },
        { key: 'payout', label: 'Payout', width: 'w-[80px]' },
        { key: 'bookie', label: 'Bookie', width: 'w-[80px]' },
        { key: 'result', label: 'Result', width: 'w-[110px]' },
        { key: 'pl', label: 'P/L', width: 'w-[90px]' }
    ];
    const colSpan = columns.length + 2; // + drag handle + actions

    return (
        <div className="bg-slate-900/50 rounded-2xl border border-slate-700/50 overflow-hidden relative">
            {isReordering && (
                <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm z-10 flex items-center justify-center rounded-2xl">
                    <div className="flex items-center gap-3 bg-slate-800 px-6 py-4 rounded-xl border border-slate-700 shadow-xl">
                        <div className="w-5 h-5 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin"></div>
                        <span className="text-white font-medium">Reordering tips...</span>
                    </div>
                </div>
            )}

            {/* Header */}
            <div className="p-4 md:p-6 border-b border-slate-700/50">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <h3 className="text-lg font-semibold text-white">Tip Log</h3>
                        {canReorder && (
                            <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 bg-slate-800/50 rounded-lg border border-slate-700/50">
                                <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                </svg>
                                <span className="text-xs text-slate-400">Drag to reorder within same tipper &amp; date</span>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                        <div className="relative">
                            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                type="text"
                                placeholder="Search tips, tippers..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 w-full sm:w-52"
                            />
                        </div>

                        <select
                            value={filterResult}
                            onChange={(e) => setFilterResult(e.target.value)}
                            className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-emerald-500"
                        >
                            <option value="all">All Results</option>
                            <option value="win">Wins</option>
                            <option value="loss">Losses</option>
                            <option value="cash out">Cashed Out</option>
                            <option value="pending">Pending</option>
                        </select>

                        <button
                            onClick={() => setIsAdding(true)}
                            disabled={isSaving}
                            className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg text-sm font-medium hover:from-emerald-500 hover:to-teal-500 transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                            </svg>
                            Add Tip
                        </button>
                    </div>
                </div>
            </div>

            {isAdding && (
                <AddTipForm
                    onSubmit={handleAddSubmit}
                    onCancel={() => setIsAdding(false)}
                    sportOptions={sportOptions}
                    tippers={tippers}
                    isSubmitting={isSaving}
                    isUnitMode={isUnitMode}
                />
            )}

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-slate-800/50">
                        <tr>
                            <th className="w-[40px] px-2 py-3">
                                {canReorder && (
                                    <div className="flex items-center justify-center" title="Drag to reorder tips within the same tipper and date">
                                        <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                                        </svg>
                                    </div>
                                )}
                            </th>
                            {columns.map(col => (
                                <th
                                    key={col.key}
                                    onClick={() => !col.noSort && handleSort(col.key)}
                                    className={`px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wider transition-colors ${
                                        col.key === 'bookie' ? 'text-center' : 'text-left'
                                    } ${col.width || ''} ${!col.noSort ? 'cursor-pointer hover:text-white' : ''}`}
                                >
                                    <div className={`flex items-center gap-1 ${col.key === 'bookie' ? 'justify-center' : ''}`}>
                                        {col.label}
                                        {!col.noSort && <SortIcon column={col.key} />}
                                    </div>
                                </th>
                            ))}
                            <th className="sticky right-0 z-20 bg-slate-800 px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider border-l border-slate-700/50">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700/50">
                        {processedTips.length === 0 ? (
                            <tr>
                                <td colSpan={colSpan} className="px-4 py-8 text-center text-slate-500">
                                    No community tips found.
                                </td>
                            </tr>
                        ) : (
                            processedTips.map((tip) => (
                                <tr
                                    key={tip.id}
                                    draggable={canReorder && editingId !== tip.id}
                                    onDragStart={(e) => handleDragStart(e, tip)}
                                    onDragEnd={handleDragEnd}
                                    onDragOver={(e) => handleDragOver(e, tip)}
                                    onDragLeave={handleDragLeave}
                                    onDrop={(e) => handleDrop(e, tip)}
                                    className={`transition-all ${
                                        canReorder && editingId !== tip.id ? 'cursor-grab active:cursor-grabbing' : ''
                                    } ${
                                        dropTargetId === tip.id
                                            ? 'bg-emerald-500/20 border-t-2 border-emerald-500'
                                            : draggedTip?.id === tip.id
                                                ? 'opacity-50'
                                                : 'hover:bg-slate-800/30'
                                    } ${
                                        tip.result === 'win' ? 'bg-emerald-500/5'
                                            : tip.result === 'loss' ? 'bg-red-500/5'
                                                : tip.result === 'cash out' ? 'bg-blue-500/5' : ''
                                    }`}
                                >
                                    {editingId === tip.id ? (
                                        // Edit mode
                                        <>
                                            <td className="w-[40px] px-2 py-3"></td>
                                            {/* Tipper (immutable) */}
                                            <td className="px-4 py-2 text-sm text-slate-300">
                                                <div className="max-w-[160px] truncate" title={getTipperLabel(tip)}>{getTipperLabel(tip)}</div>
                                            </td>
                                            {/* Tool */}
                                            <td className="px-2 py-2">
                                                <select value={editTip.tipped_tool} onChange={(e) => setEditTip(prev => ({ ...prev, tipped_tool: e.target.value }))} className={editFieldClass}>
                                                    {TOOL_OPTIONS.map(t => <option key={t} value={t}>{TOOL_LABELS[t]}</option>)}
                                                </select>
                                            </td>
                                            <td className="px-2 py-2">
                                                <input type="date" value={editTip.date} onChange={(e) => setEditTip(prev => ({ ...prev, date: e.target.value }))} className={`${editFieldClass} [color-scheme:dark] [&::-webkit-calendar-picker-indicator]:invert [&::-webkit-calendar-picker-indicator]:cursor-pointer`} />
                                            </td>
                                            <td className="px-2 py-2">
                                                <input type="text" value={editTip.game} onChange={(e) => setEditTip(prev => ({ ...prev, game: e.target.value }))} className={editFieldClass} />
                                            </td>
                                            <td className="px-2 py-2">
                                                <input type="text" value={editTip.bet} onChange={(e) => setEditTip(prev => ({ ...prev, bet: e.target.value }))} className={editFieldClass} />
                                            </td>
                                            <td className="px-2 py-2">
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={editTip.odds}
                                                    onChange={(e) => {
                                                        const odds = e.target.value;
                                                        const stake = parseFloat(editTip.stake) || 0;
                                                        const payout = odds && stake ? (parseFloat(odds) * stake).toFixed(2) : editTip.payout;
                                                        setEditTip(prev => ({ ...prev, odds, payout }));
                                                    }}
                                                    className={editFieldClass}
                                                />
                                            </td>
                                            <td className="px-2 py-2">
                                                <input
                                                    type="number"
                                                    step="0.01"
                                                    value={editTip.stake}
                                                    onChange={(e) => {
                                                        const stake = e.target.value;
                                                        const odds = parseFloat(editTip.odds) || 0;
                                                        const payout = stake && odds ? (parseFloat(stake) * odds).toFixed(2) : editTip.payout;
                                                        setEditTip(prev => ({ ...prev, stake, payout }));
                                                    }}
                                                    className={editFieldClass}
                                                />
                                            </td>
                                            <td className="px-2 py-2">
                                                <input type="number" step="0.01" value={editTip.payout} readOnly className={`${editFieldClass} bg-slate-900/50 text-slate-400 cursor-not-allowed`} title="Auto-calculated: Odds × Stake" />
                                            </td>
                                            <td className="px-2 py-2">
                                                <select value={editTip.bookie} onChange={(e) => setEditTip(prev => ({ ...prev, bookie: e.target.value }))} className={editFieldClass}>
                                                    <option value="">Select</option>
                                                    {BOOKIE_OPTIONS.map(b => <option key={b} value={b}>{b}</option>)}
                                                </select>
                                            </td>
                                            <td className="px-2 py-2">
                                                <select value={editTip.result} onChange={(e) => setEditTip(prev => ({ ...prev, result: e.target.value }))} className={editFieldClass}>
                                                    <option value="pending">Pending</option>
                                                    <option value="win">Win</option>
                                                    <option value="loss">Loss</option>
                                                    <option value="cash out">Cash Out</option>
                                                </select>
                                            </td>
                                            <td className="px-2 py-2 text-sm text-slate-400">-</td>
                                            <td className="sticky right-0 z-10 bg-[#0f172a] border-l border-slate-700/50 px-2 py-2">
                                                <div className="flex items-center gap-2">
                                                    <button onClick={handleUpdateTip} disabled={isUpdating || isSaving} className="p-1 text-emerald-400 hover:text-emerald-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                                                        {isUpdating && editingId === tip.id ? (
                                                            <div className="w-4 h-4 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin"></div>
                                                        ) : (
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                                        )}
                                                    </button>
                                                    <button onClick={() => { setEditingId(null); setEditTip(null); }} disabled={isUpdating} className="p-1 text-slate-400 hover:text-white transition-colors disabled:opacity-50">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                                    </button>
                                                </div>
                                            </td>
                                        </>
                                    ) : (
                                        // View mode
                                        <>
                                            <td className="w-[40px] px-2 py-3">
                                                <div className={`flex items-center justify-center ${canReorder ? 'cursor-grab active:cursor-grabbing text-slate-500 hover:text-slate-300' : 'text-slate-600'} transition-colors`} title={canReorder ? 'Drag to reorder' : 'Clear filters to enable reordering'}>
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8h16M4 16h16" /></svg>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm">
                                                <div className="flex items-center gap-2 max-w-[160px]">
                                                    <span className="w-6 h-6 flex-shrink-0 rounded-full bg-slate-700/60 text-slate-300 text-[10px] font-bold flex items-center justify-center uppercase">
                                                        {getTipperLabel(tip).charAt(0)}
                                                    </span>
                                                    <span className="text-slate-200 font-medium truncate" title={getTipperLabel(tip)}>{getTipperLabel(tip)}</span>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-700/40 text-slate-300 border border-slate-600/40 whitespace-nowrap">
                                                    {TOOL_LABELS[tip.tipped_tool] || tip.tipped_tool || '—'}
                                                </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-300 whitespace-nowrap">{formatDate(tip.date)}</td>
                                            <td className="px-4 py-3 text-sm text-white font-medium">
                                                <div className="max-w-[160px] flex items-center gap-1.5">
                                                    <span className="truncate" title={tip.game}>{tip.game}</span>
                                                    {tip.sport === 'afl' && tip.round != null && String(tip.round).trim() !== '' && (
                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold bg-slate-700/40 text-slate-400 border border-slate-600/30 whitespace-nowrap flex-shrink-0">R{String(tip.round).replace(/[^0-9]/g, '')}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-300 align-top">
                                                <div className="min-w-[200px] max-w-[340px] whitespace-normal break-words leading-snug" title={tip.bet}>{tip.bet}</div>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-slate-300 font-mono">{Number(tip.odds).toFixed(2)}</td>
                                            <td className="px-4 py-3 text-sm text-slate-300">{formatStaked(tip.stake)}</td>
                                            <td className="px-4 py-3 text-sm text-slate-300">{tip.payout > 0 ? formatStaked(tip.payout) : '-'}</td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center justify-center">
                                                    {bookieLogos[tip.bookie] ? (
                                                        <img src={bookieLogos[tip.bookie]} alt={tip.bookie} className="w-6 h-6 rounded object-cover" title={tip.bookie} />
                                                    ) : (
                                                        <span className="text-sm text-slate-300">{tip.bookie}</span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="relative">
                                                    {updatingResultId === tip.id ? (
                                                        <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-slate-500/20 text-slate-400 border border-slate-500/30">
                                                            <div className="w-3 h-3 border-2 border-slate-400/30 border-t-slate-400 rounded-full animate-spin mr-1.5"></div>
                                                            Updating...
                                                        </div>
                                                    ) : (
                                                        <select
                                                            value={tip.result}
                                                            onChange={(e) => handleQuickResultChange(tip, e.target.value)}
                                                            disabled={isSaving || deletingId === tip.id || editingId === tip.id}
                                                            className={`appearance-none cursor-pointer px-2.5 py-1 pr-6 rounded-full text-xs font-medium border focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-offset-slate-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                                                                tip.result === 'win' ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30 focus:ring-emerald-500/50'
                                                                    : tip.result === 'loss' ? 'bg-red-500/20 text-red-400 border-red-500/30 focus:ring-red-500/50'
                                                                        : tip.result === 'cash out' ? 'bg-blue-500/20 text-blue-400 border-blue-500/30 focus:ring-blue-500/50'
                                                                            : 'bg-amber-500/20 text-amber-400 border-amber-500/30 focus:ring-amber-500/50'
                                                            }`}
                                                            style={{
                                                                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                                                backgroundRepeat: 'no-repeat',
                                                                backgroundPosition: 'right 4px center',
                                                                backgroundSize: '12px'
                                                            }}
                                                        >
                                                            <option value="pending">Pending</option>
                                                            <option value="win">Win</option>
                                                            <option value="loss">Loss</option>
                                                            <option value="cash out">Cash Out</option>
                                                        </select>
                                                    )}
                                                </div>
                                            </td>
                                            <td className={`px-4 py-3 text-sm font-bold ${tip.pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                                {formatCurrency(tip.pl)}
                                            </td>
                                            <td className="sticky right-0 z-10 bg-[#0f172a] border-l border-slate-700/50 px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    <button onClick={() => startEditing(tip)} disabled={isSaving || deletingId === tip.id} className="p-1 text-slate-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed" title="Edit">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                                                    </button>
                                                    <button onClick={() => handleDelete(tip.id)} disabled={deletingId === tip.id || isSaving} className="p-1 text-slate-400 hover:text-red-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" title="Delete">
                                                        {deletingId === tip.id ? (
                                                            <div className="w-4 h-4 border-2 border-red-400/30 border-t-red-400 rounded-full animate-spin"></div>
                                                        ) : (
                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                                        )}
                                                    </button>
                                                </div>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Summary Footer */}
            {processedTips.length > 0 && (
                <div className="p-4 bg-slate-800/30 border-t border-slate-700/50">
                    <div className="flex flex-wrap items-center justify-between gap-4 text-sm">
                        <div className="text-slate-400">
                            Showing <span className="text-white font-medium">{processedTips.length}</span> of <span className="text-white font-medium">{tips.length}</span> tips
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="text-slate-400">
                                Total Staked: <span className="text-white font-medium">{formatTotalStaked(searchFilteredTips)}</span>
                            </div>
                            <div className="text-slate-400">
                                Total P/L: <span className={`font-semibold ${getTotalPLValue(searchFilteredTips) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {formatTotalPL(searchFilteredTips)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Cash Out Modal */}
            {showCashOutModal && cashOutTip && createPortal(
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]" onClick={(e) => e.target === e.currentTarget && handleCashOutCancel()}>
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-sm mx-4 shadow-2xl">
                        <div className="flex items-center justify-between mb-5">
                            <h3 className="text-lg font-semibold text-white">Cash Out Tip</h3>
                            <button onClick={handleCashOutCancel} className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/50 transition-all">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>

                        <div className="mb-4 p-3 bg-slate-800/50 rounded-xl border border-slate-700/30">
                            <div className="text-xs text-slate-500 mb-1">Bet</div>
                            <div className="text-sm text-white font-medium truncate" title={cashOutTip.bet}>{cashOutTip.bet}</div>
                            <div className="text-xs text-slate-500 mt-1">{cashOutTip.game} · {getTipperLabel(cashOutTip)}</div>
                        </div>

                        <div className="flex gap-3 mb-4">
                            <div className="flex-1 p-3 bg-slate-800/30 rounded-xl border border-slate-700/30">
                                <div className="text-xs text-slate-500 mb-1">Stake</div>
                                <div className="text-white font-semibold">{formatStaked(cashOutTip.stake)}</div>
                            </div>
                            <div className="flex-1 p-3 bg-slate-800/30 rounded-xl border border-slate-700/30">
                                <div className="text-xs text-slate-500 mb-1">Potential Payout</div>
                                <div className="text-slate-300 font-semibold">{formatStaked(cashOutTip.stake * cashOutTip.odds)}</div>
                            </div>
                        </div>

                        <div className="mb-4">
                            <label className="block text-sm text-slate-300 mb-2">Cash Out Amount {isUnitMode ? '(units)' : ''}</label>
                            <div className="relative">
                                {!isUnitMode && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>}
                                <input
                                    ref={cashOutInputRef}
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={cashOutAmount}
                                    onChange={(e) => setCashOutAmount(e.target.value)}
                                    placeholder="0.00"
                                    className={`w-full ${isUnitMode ? 'pl-4' : 'pl-7'} pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 transition-colors`}
                                />
                            </div>
                        </div>

                        {cashOutAmount !== '' && !isNaN(parseFloat(cashOutAmount)) && (
                            <div className={`flex items-center justify-between p-3 rounded-xl mb-4 ${
                                parseFloat(cashOutAmount) - cashOutTip.stake >= 0 ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-red-500/10 border border-red-500/20'
                            }`}>
                                <span className="text-sm text-slate-300">Calculated P/L</span>
                                <span className={`font-semibold ${parseFloat(cashOutAmount) - cashOutTip.stake >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {formatCurrency(parseFloat(cashOutAmount) - cashOutTip.stake)}
                                </span>
                            </div>
                        )}

                        <div className="flex gap-3 mt-5">
                            <button onClick={handleCashOutCancel} className="flex-1 px-4 py-2.5 text-slate-400 hover:text-white text-sm font-medium transition-colors rounded-xl hover:bg-slate-800/50">Cancel</button>
                            <button
                                onClick={handleCashOutConfirm}
                                disabled={cashOutAmount === '' || isNaN(parseFloat(cashOutAmount)) || parseFloat(cashOutAmount) < 0}
                                className="flex-1 px-4 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl text-sm font-medium hover:from-emerald-500 hover:to-teal-500 transition-all shadow-lg shadow-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none"
                            >
                                Confirm Cash Out
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}

export default CommunityBetTracker;
